import { Box, Button, Link, Typography, useTheme } from '@mui/joy';
import { Chapter } from '../../types';
import {
	DonorsChapterPreview,
	NationalChapterPreview,
	StateChapterPreview,
	AnalysisChapterPreview
} from '../Chapters/ChapterPreviews';
import openSecrets from '../../assets/os-logo-color.png';
import { Divider } from '.';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useChapterConfig, ChapterType } from '../../hooks/useChapterConfig';

type OtherPagesProps = {
	currentChapter: Chapter['chapter'];
};

export const OtherPages = ({ currentChapter }: OtherPagesProps) => {
	const theme = useTheme();
	const { getEnabledChapters } = useChapterConfig();

	// Get all enabled chapters except the current one
	// Filter by InternalChapter types only (exclude 'default' and undefined)
	const validChapterTypes: Array<'candidates' | 'opportunities' | 'donors' | 'analysis'> =
		['candidates', 'opportunities', 'donors', 'analysis'];
	const otherChapters = validChapterTypes.includes(currentChapter as any)
		? getEnabledChapters(currentChapter as 'candidates' | 'opportunities' | 'donors' | 'analysis')
		: getEnabledChapters();

	const isAnalysis = currentChapter === 'analysis';
	const matchesMd = useMediaQuery(theme.breakpoints.up(900));
	const matchesLg = useMediaQuery(theme.breakpoints.up(1150));

	const getPreviewComponent = (
		chapterType: ChapterType,
		disabled: boolean,
		disabledText: string | undefined,
		url: string | undefined
	) => {
		switch (chapterType) {
			case 'state_view':
				return <StateChapterPreview key={chapterType} disabled={disabled} disabledText={disabledText} href={url}/>;
			case 'national_view':
				return <NationalChapterPreview key={chapterType} disabled={disabled} disabledText={disabledText} href={url}/>;
			case 'donor_gaps':
				return <DonorsChapterPreview key={chapterType} disabled={disabled} disabledText={disabledText} href={url}/>;
			case 'final_analysis':
				return <AnalysisChapterPreview key={chapterType} disabled={disabled} disabledText={disabledText} href={url}/>;
			default:
				return null;
		}
	};

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection:
						isAnalysis && !matchesLg ? 'column' : !matchesMd ? 'column' : 'row',
					// xs: 'column',
					// md: isAnalysis ? 'column' : 'row',
					// lg: 'row'

					gap: isAnalysis ? '60px' : '113px',
					alignItems:
						isAnalysis && matchesLg
							? 'stretch'
							: isAnalysis && matchesMd
							? 'center'
							: { xs: 'center', md: 'stretch' },
					justifyContent: 'center',
					position: 'relative',
					marginTop: '135px',
					maxWidth: '680px',
					marginX: 'auto'
				}}
			>
				{otherChapters.map((chapter) => getPreviewComponent(chapter.type, chapter.disabled, chapter.disabledText, chapter.url))}
			</Box>
			<Box
				sx={{
					textAlign: 'center',
					marginX: 'auto',
					marginTop: '95px'
				}}
			>
				<Link
					href={`https://cawp.rutgers.edu/research/cawp-women-money-and-politics-series`}
					target='_blank'
					underline='none'
					sx={{ ':hover': { textDecoration: 'none' } }}
				>
					<Button
						sx={{
							':hover': {
								backgroundColor: '#0043B0',
								textDecoration: 'none'
							},
							backgroundColor: theme.palette.controls.controlFill,
							fontSize: '1.4375rem',
							fontWeight: 'bold',
							padding: '10px',
							paddingX: '45px'
						}}
					>
						CAWP Women, Money, and Politics Series
					</Button>
				</Link>

				<Typography
					level='body2'
					sx={{ marginTop: '42px', marginBottom: '6px' }}
				>
					Data fueled in part by:
				</Typography>
				<a href='https://www.opensecrets.org/' target='__blank'>
					<img
						src={openSecrets}
						style={{ margin: 'auto', marginBottom: '72px', width: '194px' }}
						alt='Open Secrets logo'
					/>
				</a>
			</Box>
			{currentChapter !== 'analysis' && <Divider />}
		</>
	);
};
